import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import * as moment from "moment";
import PriceAction from "./components/PriceAction/PriceAction";
import PriceType from "./components/PriceType/PriceType";
import CatchmentArea from "./components/CatchmentArea/CatchmentArea";
import Transports from "./components/Transports/Transports";
import ValidityDate from "./components/ValidityDate/ValidityDate";
import Price from "./components/Price/Price";
import Label from "./components/Label/Label";
import {
  TABLE_CENTER_PRODUCT_PRICE_LIST,
  CENTER_PRODUCT_PRICE_LIST_ENDPOINT,
  FILTER_TYPE_BUTTON,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import { loadAllCenterProductPrice } from "./PriceList.actionCreators";
import { setAllCenterProductPriceLoadingState } from "./PriceList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import { renderPriceWithCurrency, textReplace, isEmpty } from "@utils/utils";
import UserInfo from "@utils/UserInfo";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import CustomColumnFilter from "@shared/baseUI/CustomColumnFilter/CustomColumnFilter";
import { DivOrangeBackground } from "./PriceList.styled";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    centreId,
    companyId,
    centreProductId,
    centerProductStatus,
    createCenterProductPrice,
    transportTypes,
    countryId,
  } = props;

  const [data, setData] = useState([]);
  const [status, setStatus] = useState(1);
  const [isMemoryPrice, setIsMemoryPrice] = useState(0);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);

  let dataURL = CENTER_PRODUCT_PRICE_LIST_ENDPOINT;

  const payloadData = {
    "{centreId}": centreId,
    "{centreProductId}": centreProductId,
    "{companyId}": companyId,
    "{countryId}": countryId,
  };

  Object.keys(payloadData).forEach((key) => {
    dataURL = textReplace(dataURL, key, payloadData[key]);
  });

  const dispatch = useDispatch();
  const refreshData = (data) => {
    setRefreshState(data);
  };

  let columns = [
    {
      Header: t("PERIOD"),
      title: "PERIOD",
      accessor: "period",
      Cell: ({ cell: { value } }) => <ValidityDate data={value} />,
      disableFilters: true,
    },
    {
      Header: t("PRICE_TYPE"),
      title: "PRICE_TYPE",
      accessor: "priceType",
      Cell: ({ cell: { value } }) => <PriceType data={value} />,
      disableFilters: true,
    },
    {
      Header: t("LABEL"),
      title: "LABEL",
      accessor: "label",
      Cell: ({ cell: { value } }) => <Label data={value} />,
      disableFilters: true,
    },
    {
      Header: t("PRICE"),
      title: "PRICE",
      accessor: "price",
      Cell: ({ cell: { value } }) => <Price data={value} />,
      disableFilters: true,
    },
    {
      Header: t("TRANSPORT_TYPES"),
      title: "TRANSPORT_TYPES",
      accessor: "transportTypes",
      Cell: ({ cell: { value } }) => <Transports data={value} />,
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("PRICES_FILTER_TRANSPORT_TYPE")}
          name="transportTypes"
          labelClass="center-product-price-label-transport-label"
          containerClass="center-product-price-list__transport-list-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={transportTypes}
          placeholder={t("SEARCH")}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("CATCHMENT_AREA"),
      title: "CATCHMENT_AREA",
      accessor: "catchmentArea",
      Cell: ({ cell: { value, column } }) => (
        <CatchmentArea
          data={value}
          centerProductStatus={centerProductStatus}
          updateRefeshStatus={updateRefeshStatus}
          transportTypes={transportTypes}
          companyId={companyId}
          centerId={centreId}
          centerProductId={centreProductId}
          countryId={countryId}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <PriceAction
          data={value}
          column={column}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
          transportTypes={transportTypes}
          companyId={companyId}
          centerId={centreId}
          centerProductId={centreProductId}
          countryId={countryId}
        />
      ),
      disableFilters: true,
    },
  ];

  const centerProductPriceListByIds = useSelector(
    (state) => state.centerProductPriceList.byId
  );
  const centerProductPriceListAllIds = useSelector(
    (state) => state.centerProductPriceList.allIds
  );
  const filteredRecords = useSelector(
    (state) => state.centerProductPriceList.filteredRecords
  );

  const isCenterProductPriceListLoading = useSelector(
    (state) => state.centerProductPriceList.isLoading
  );

  useEffect(() => {
    setLoading(isCenterProductPriceListLoading);
  }, [isCenterProductPriceListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });
    let transports = [];

    if (appliedFilters !== undefined) {
      const transportFilter = appliedFilters.filter(
        (filterItem) => filterItem.column === "transport"
      );
      transports = transportFilter?.map((item) => item.value);
      if (transports === "*") {
        transports = transportTypes.map((item) => item.value);
      }
    }
    // Set the loading state
    setLoading(true);
    dispatch(setAllCenterProductPriceLoadingState());
    dispatch(
      loadAllCenterProductPrice({
        dataURL,
        transports,
        isMemoryPrice,
        companyId,
        centreId,
        centreProductId,
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        status,
        appliedFilters,
        countryId,
      })
    );
  };

  useEffect(() => {
    if (centerProductPriceListAllIds.length > 0) {
      const responseData = centerProductPriceListAllIds.map((id) => {
        const tempData = { ...centerProductPriceListByIds[id] };
        tempData.period = {
          validityStartDate: tempData.validityStartDate || "",
          validityEndDate: tempData.validityEndDate || "",
          isActive: tempData.isActive,
          isMemoryPrice: tempData.isMemoryPrice,
        };

        tempData.priceType = {
          type: tempData.priceType,
          isActive: tempData.isActive,
        };
        tempData.label = {
          label: tempData.label ? tempData.label : "-",
          isActive: tempData.isActive,
        };
        tempData.priceText = renderPriceWithCurrency(
          tempData.price,
          t(tempData.unit),
          tempData.currency?.symbol,
          tempData.currency?.position
        );

        tempData.price = {
          price: tempData.priceText,
          isActive: tempData.isActive,
        };
        if (tempData.catchmentArea.catchmentType === 2) {
          const departmentList =
            tempData.catchmentArea.catchmentValue.split(",");
          tempData.catchmentArea = {
            departmenList: departmentList.join(","),
            sdepartment: departmentList.slice(0, 10).join(","),
            count: departmentList.length - 10,
            catchmentType: 2,
            id: tempData.id,
          };
        }
        tempData.catchmentArea = {
          ...tempData.catchmentArea,
          isActive: tempData.isActive,
          id: tempData.id,
        };
        let nameList = tempData.transportTypes?.reduce(
          (p, c) => [...p, c?.name],
          []
        );
        let sname = nameList?.slice(0, 3);
        tempData.transportTypes = {
          completeName: nameList?.join(", "),
          name: sname?.join(","),
          count: nameList ? nameList.length - 3 : 0,
          isActive: tempData.isActive,
          id: tempData.id,
        };
        tempData.action = {
          id: tempData.id,
          isActive: tempData.isActive,
          companyId,
          centreId,
          centreProductId,
          centreProductStatus: centerProductStatus,
        };
        tempData.rowStyle =
          tempData.isMemoryPrice && tempData.isActive
            ? DivOrangeBackground
            : null;
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [
    t,
    centerProductPriceListAllIds,
    centerProductPriceListByIds,
    filteredRecords,
  ]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const changeStatus = (label) => {
    if (label === "memory") {
      let memory = isMemoryPrice === 1 ? 0 : 1;
      setIsMemoryPrice(memory);
    } else {
      let statusNew = status === 1 ? 0 : 1;
      setStatus(statusNew);
    }
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        appliedFilters: requestData.appliedFilters,
      });
    }
  }, [status, isMemoryPrice]);

  return (
    <>
      <EncloseTable
        type={TABLE_CENTER_PRODUCT_PRICE_LIST}
        columns={columns}
        data={data}
        setData={setData}
        createNewAction={createCenterProductPrice}
        initialPageSize={30}
        isRefreshDataAgain={isRefreshDataAgain}
        setSkipPageReset={setSkipPageReset}
        skipPageReset={skipPageReset}
        pageCount={pageCount}
        currentPageToFirst={currentPageToFirst}
        setCurrentPageToFirst={setCurrentPageToFirst}
        setPageCount={setPageCount}
        fetchRecords={fetchRecords}
        loading={loading}
        defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
        extraFilters={[
          {
            type: FILTER_TYPE_BUTTON,
            label: "PRICES_FILTER_ACTIVE_ONLY",
            name: "prices",
            labelClass: "center-product-price-list__status-label",
            containerClass: "center-product-price-list__status-container",
            imageEnableIcon: enableIcon,
            imageDisableIcon: disableIcon,
            status,
            changeStatus: { changeStatus },
          },
          {
            type: FILTER_TYPE_BUTTON,
            label: "PRICES_FILTER_MEMORY_PRICE",
            name: "memory",
            labelClass: "center-product-price-list__memory-label",
            containerClass: "center-product-price-list__memory-container",
            imageEnableIcon: enableIcon,
            imageDisableIcon: disableIcon,
            status: isMemoryPrice,
            changeStatus: { changeStatus },
          },
        ]}
      />
    </>
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
};

export default withTranslation()(PrepareDataTable);
